<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Linklist } from '#library/components.schema.build.ts'
	import Render from '#components/render.svelte'
	import Link from '#components/link.svelte'
	import Filterbar from '#components/filterbar.svelte'
	import { slide } from 'svelte/transition'
	import { params } from '#library/params.svelte.ts'
	import { page } from '$app/state'

	interface Props {
		blok: Blok<Linklist>
	}

	let { blok }: Props = $props()

	let active = $state(false)

	const policy_type = $derived(page.url.searchParams.get('policy-type'))

	const filters = $derived.by(() => {
		const set = [...new Set(blok.items?.map(e => e.category).filter(Boolean))]
		if (!set.length) return []

		return set.map((item, index) => ({
			label: item!,
			href: params({ 'policy-type': item! }),
			active: policy_type === item || (!policy_type && index === 0)
		}))
	})

	const results = $derived.by(() => {
		const has_category = blok.items?.some(e => e.category)
		const filtered =
			has_category && policy_type
				? blok.items?.filter(e => e.category === policy_type)
				: has_category
					? blok.items?.filter(e => e.category === filters[0]?.label)
					: blok.items

		const search = page.url.searchParams.get('search')

		if (search) {
			return filtered?.filter(e => e.link?.text?.toLowerCase().includes(search.toLowerCase()))
		}

		return filtered
	})
</script>

<section class="linklist {blok.theme || 'theme-rose-on-white'}" {...attrs(blok)}>
	<div class="contain {blok.split ? `split-${blok.split}` : ''}">
		<div class="content" class:fill={filters.length}>
			{#if blok.title}
				<div class="title {blok.size}" class:plain={blok.plaintitle} class:fill={filters.length}>
					{blok.title}
				</div>
			{/if}
			{#if blok.body}
				<div class="body">
					<Render content={blok.body} />
				</div>
			{/if}
		</div>
		{#if results}
			{#if filters.length}
				<div class="categories">
					<Filterbar {filters} />
				</div>
			{/if}
			{#if results.length}
				<div class="links" class:plain={blok.plain}>
					{#each results.slice(0, 12) as item, i (i)}
						{#if item.link?.url}
							<Link
								href={item.link.url}
								text={item.link.text ?? ''}
								target={item.link.target}
								large={!blok.shrink}
								medium={blok.shrink}
								subtitle={item.subtitle}
							/>
						{/if}
					{/each}
					{#if results.length > 12}
						<div>
							<button class="more" onclick={() => (active = !active)}
								>Show {active ? 'less' : `all (${results.length - 12} more)`}</button
							>
							{#if active}
								<div class="items" transition:slide>
									{#each results.slice(12) as item, i (i)}
										{#if item.link?.url}
											<Link
												href={item.link.url}
												text={item.link.text ?? ''}
												target={item.link.target}
												large={!blok.shrink}
												medium={blok.shrink}
												subtitle={item.subtitle}
											/>
										{/if}
									{/each}
								</div>
							{/if}
						</div>
					{/if}
				</div>
			{:else}
				<p>No results found</p>
			{/if}
		{/if}
	</div>
</section>

<style lang="css">
	.linklist {
		background-color: var(--c-background);
		color: var(--c-foreground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.categories {
		grid-column: 1 / -1;
	}

	.contain {
		display: flex;
		flex-direction: column;
		gap: 1rem;

		@media (--from-mobile) {
			display: grid;
			gap: 2rem;
			grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

			&.split-7 {
				grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);

				&.flip {
					grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
				}
			}

			&.split-8 {
				grid-template-columns: minmax(0, 4fr) minmax(0, 8fr);

				&.flip {
					grid-template-columns: minmax(0, 8fr) minmax(0, 4fr);
				}
			}
		}
	}

	.content {
		&.fill {
			grid-column: 1 / -1;
		}
	}

	.title {
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
		max-width: 10ch;

		&.plain {
			color: var(--c-midground);
		}

		&.small {
			font: 600 var(--f-h4);
			letter-spacing: var(--ls-h4);
			max-width: 10ch;
		}

		&.medium {
			font: 600 var(--f-h3);
			letter-spacing: var(--ls-h3);
			max-width: 12ch;
		}

		&.fill {
			max-width: 22ch;
		}
	}

	.subtitle {
		color: var(--c-midground);
		font: 500 var(--f-h3);
		letter-spacing: var(--ls-h3);
	}

	.body {
		color: var(--c-midground);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-block-start: 1.5rem;
		max-width: 36ch;
	}

	.links {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		grid-column: 2;

		&.plain {
			--c-foreground: var(--c-midground);
		}

		:global(.anchor) {
			max-width: none;
		}
	}

	.more {
		align-self: start;
		margin-block-start: 2.5rem;
		text-align: start;
	}
</style>
